import type { AdminMenu } from '@/types/auth'

export type AdminMenuScope = 'business' | 'countrySite'

export interface AdminMenuItem {
  key: string
  title: string
  icon: string
  path?: string
  menuCode?: string
  sortOrder: number
  /** 站点路由配置页传给后端的页面类型。 */
  pageType?: string
  /** 后端可见菜单尚未配置时，由前端按 `/auth/me` 有效权限临时补入并继承指定访问范围。 */
  authorizationFallbackCode?: string
  authorizationFallbackCodes?: string[]
  /** 菜单仍声明在兼容目录时，指定前端补入入口实际所属的顶级目录。 */
  frontendScope?: AdminMenuScope
  /** 复用锦标赛页面时用于选择 Daily/Weekly 接口。 */
  tournamentKind?: 'daily' | 'weekly'
  children?: AdminMenuItem[]
}

/** 当前统一管理后台菜单正在使用的 Element Plus 图标。 */
export const adminMenuIconOptions = [
  'Checked',
  'Bell',
  'CollectionTag',
  'DataAnalysis',
  'Document',
  'HomeFilled',
  'Monitor',
  'OfficeBuilding',
  'Present',
  'Promotion',
  'Setting',
  'SetUp',
  'Tickets',
  'Trophy',
  'User'
] as const

const item = (
  key: string,
  title: string,
  path: string,
  menuCode: string,
  sortOrder: number,
  icon = 'Document'
): AdminMenuItem => ({ key, title, path, menuCode, sortOrder, icon })

const clientPageItem = (
  key: string,
  title: string,
  path: string,
  menuCode: string,
  sortOrder: number,
  pageType: string
): AdminMenuItem => ({
  ...item(key, title, path, menuCode, sortOrder),
  pageType,
  authorizationFallbackCode: 'site:client-page:query'
})

/** 路由目录；实际侧边栏、菜单管理和角色权限树层级以数据库返回的两个顶级目录为准。 */
export const adminMenuCatalog: Record<AdminMenuScope, AdminMenuItem[]> = {
  business: [
    {
      key: 'business-users',
      title: '用户管理',
      icon: 'User',
      sortOrder: 70,
      children: [
        item('business-user-list', '用户列表', '/business/users/list', 'site:client-user:query', 10),
        item('country-site-author-config', '作者管理', '/country-site/authors', 'site:author:query', 20, 'User')
      ]
    },
    {
      key: 'business-records',
      title: '信息记录',
      icon: 'DataAnalysis',
      sortOrder: 80,
      children: [
        item('business-coin-records', '金币记录', '/business/records/coins', 'site:transaction:query', 10),
        item('business-cash-records', '钻石记录', '/business/records/cash', 'business:record:cash', 20),
        // 暂缓开发，恢复时取消注释即可重新注册路由和菜单入口。
        // item('business-deposit-records', '充值记录', '/business/records/deposits', 'business:record:deposit', 30),
        item('business-withdrawal-records', '提现记录', '/business/records/withdrawals', 'site:withdrawal:query', 40),
        item(
          'business-ticket-records',
          '门票记录',
          '/business/records/tournament-tickets',
          'business:record:tournament-ticket',
          50
        ),
        item('business-sent-message-records', '站内信', '/business/records/sent-messages', 'site:message:query', 60),
        item(
          'business-contact-feedback-records',
          '反馈记录',
          '/business/records/contact-feedback',
          'site:contact-feedback:query',
          70
        )
      ]
    },
    {
      key: 'business-casinos',
      title: '盘口管理',
      icon: 'OfficeBuilding',
      sortOrder: 40,
      children: [
        item('business-casino-list', 'Casino管理', '/business/casinos', 'center:odds:query', 10),
        item('business-payment-methods', '支付方式', '/business/casinos/payment-methods', 'center:odds-payment-way:query', 20),
        item('business-casino-licenses', '牌照管理', '/business/casinos/licenses', 'center:odds-license:query', 30)
      ]
    },
    {
      key: 'business-bonuses',
      title: '礼金管理',
      icon: 'Present',
      sortOrder: 50,
      children: [item('business-bonus-list', 'Bonus管理', '/business/bonuses', 'center:odds-activity:query', 10)]
    },
    {
      key: 'business-games',
      title: '游戏管理',
      icon: 'Trophy',
      sortOrder: 60,
      children: [
        item('business-demo-games', 'Demo游戏', '/business/games/demo', 'center:demo-game:query', 10),
        item('business-coin-games', '金币游戏', '/business/games/coin', 'center:coin-game:query', 20),
        item('business-game-providers', '供应商管理', '/business/games/providers', 'center:game-provider:query', 30),
        item('business-game-categories', '游戏分类', '/business/games/categories', 'center:game-classification:query', 40),
        item('business-game-features', '游戏特色', '/business/games/features', 'center:game-mechanics:query', 50),
        item('business-game-atlases', '游戏图鉴', '/business/games/atlases', 'center:game-atlas:query', 60, 'Picture'),
        item(
          'business-game-atlas-categories',
          '图鉴类别',
          '/business/games/atlas-categories',
          'center:game-atlas-category:query',
          65,
          'Collection'
        ),
        item(
          'business-game-ip-topics',
          '游戏IP主题',
          '/business/games/ip-topics',
          'center:demo-game-topic:query',
          70,
          'CollectionTag'
        )
      ]
    },
    {
      key: 'business-activities',
      title: '活动管理',
      icon: 'Promotion',
      sortOrder: 90,
      children: [
        item(
          'business-sports-predictions',
          '体育竞猜',
          '/business/activities/sports-predictions',
          'business:activity:sports-prediction',
          40
        )
      ]
    },
    {
      key: 'business-content',
      title: '内容管理',
      icon: 'Document',
      sortOrder: 100,
      children: [
        item('business-posts', '帖子管理', '/business/content/posts', 'site:post:query', 10),
        item('business-comments', '评论管理', '/business/content/comments', 'site:post-comment:query', 20),
        item('business-topics', '话题管理', '/business/content/topics', 'site:post-topic:query', 30),
        // 暂缓开发，恢复时取消注释即可重新注册路由和菜单入口。
        // item(
        //   'business-sensitive-words',
        //   '敏感词管理',
        //   '/business/content/sensitive-words',
        //   'business:content:sensitive-word',
        //   50
        // ),
        item('business-blocked-users', '小黑屋管理', '/business/content/blocked-users', 'business:content:blocked-user', 60),
        item('business-odds-ratings', '盘口评价管理', '/business/content/odds-ratings', 'site:review:query', 80),
        {
          ...item(
            'business-game-ratings',
            '游戏评价管理',
            '/business/content/game-ratings',
            'business:content:game-rating',
            85
          ),
          authorizationFallbackCode: 'site:review:query',
          frontendScope: 'countrySite'
        },
        item('business-articles', '文章管理', '/business/content/articles', 'center:news-article:query', 90),
        item('business-news-topics', '新闻专题', '/business/content/news-topics', 'center:news-topic:query', 95),
        item('business-newsletters', '每周简报', '/business/content/newsletters', 'center:newsletter:query', 97),
        item('business-news-authors', '新闻作者', '/business/content/news-authors', 'center:news-author:query', 100),
        item('business-news-feedbacks', '反馈记录', '/business/content/news-feedbacks', 'center:news-feedback:query', 110)
      ]
    },
    {
      key: 'business-audits',
      title: '审核管理',
      icon: 'Checked',
      sortOrder: 110,
      children: [
        item('business-post-audits', '帖子审核', '/business/audits/posts', 'site:post-review:query', 10),
        item('business-comment-audits', '评论审核', '/business/audits/comments', 'site:post-comment-review:query', 20),
        {
          ...item('business-withdrawal-audits', '提现审核', '/business/audits/withdrawals', 'business:audit:withdrawal', 30),
          authorizationFallbackCode: 'site:withdrawal:query'
        },
        item('business-fb-share-audits', 'FB分享审核', '/business/audits/fb-shares', 'site:fb-share-review:query', 40),
        item('business-complaint-audits', '投诉审核', '/business/audits/complaints', 'site:complaint:query', 50),
        {
          ...item(
            'business-game-rating-audits',
            '游戏评价审核',
            '/business/audits/game-ratings',
            'business:audit:game-rating',
            60
          ),
          authorizationFallbackCode: 'business:audit:rating'
        },
        {
          ...item('business-odds-audits', '盘口评价审核', '/business/audits/odds', 'business:audit:odds', 70),
          authorizationFallbackCode: 'business:audit:rating'
        }
      ]
    },
    {
      key: 'business-config',
      title: '配置管理',
      icon: 'SetUp',
      sortOrder: 100,
      children: [
        item('business-countries', '国家管理', '/business/config/countries', 'center:country:query', 10),
        item('business-languages', '语言管理', '/business/config/languages', 'center:language:query', 20),
        item('business-currencies', '货币管理', '/business/config/currencies', 'center:currency:query', 30),
        item('business-exchange-rates', '汇率管理', '/business/config/exchange-rates', 'center:exchange-rate:query', 40),
        item('business-message-records', '站内信模板', '/business/config/message-templates', 'center:message-template:query', 50),
        item('business-characters', '人物类别', '/business/config/characters', 'center:author-config:query', 60),
        item(
          'business-responsible-gambling',
          '负责任博彩',
          '/business/config/responsible-gambling',
          'center:responsible-gambling:query',
          70
        ),
        item('business-lucky-wheel', '幸运转盘管理', '/business/config/lucky-wheel', 'center:lucky-wheel:query', 80, 'Present'),
        item('business-slot-machine', '老虎机抽奖管理', '/business/config/slot-machine', 'center:slot-machine:query', 90, 'Monitor'),
        item('business-lucky-scratch', '刮刮乐奖池管理', '/business/config/lucky-scratch', 'center:lucky-scratch:query', 95, 'Tickets'),
        item(
          'business-coin-shop-exchanges',
          '金币商城兑换配置',
          '/business/config/coin-shop-exchanges',
          'center:coin-shop-exchange:query',
          98,
          'ShoppingCart'
        ),
        item(
          'business-project-global-config',
          '全局配置管理',
          '/business/config/project-global',
          'center:project-global-config:query',
          100,
          'Setting'
        ),
        item(
          'business-invitee-tasks',
          '邀请人数宝箱配置',
          '/business/config/invitee-tasks',
          'center:invitee-task:query',
          105,
          'Box'
        ),
        item(
          'business-payout-payment-ways',
          '提现支付方式管理',
          '/business/config/payment-ways',
          'center:payment-way:query',
          110,
          'Tickets'
        ),
        item('business-news-notices', '新闻公告管理', '/business/config/news-notices', 'center:news-notice:query', 75, 'Bell')
        // 暂缓开发，恢复时取消注释即可重新注册路由和菜单入口。
        // item('business-payment-channels', '支付渠道', '/business/config/payment-channels', 'business:config:payment-channel', 50),
        // item('business-time-zones', '时区管理', '/business/config/time-zones', 'business:config:time-zone', 60)
      ]
    },
    {
      key: 'business-system',
      title: '系统管理',
      icon: 'Setting',
      sortOrder: 110,
      children: [
        item('system-users', '用户管理', '/business/system/users', 'center:admin-user:manage', 10),
        item('system-roles', '角色管理', '/business/system/roles', 'center:role:manage', 20),
        item('system-menus', '菜单管理', '/business/system/menus', 'center:menu:manage', 30),
        item('system-operation-logs', '操作日志', '/business/system/operation-logs', 'center:operation-log:list', 40)
      ]
    }
  ],
  countrySite: [
    item('country-site-list', '站点管理', '/country-site/sites', 'site:site:query', 10, 'OfficeBuilding'),
    {
      key: 'country-site-route-config',
      title: '路由配置',
      icon: 'Promotion',
      sortOrder: 20,
      children: [
        clientPageItem(
          'country-site-country-routes',
          '国家首页路由',
          '/country-site/routes/country',
          'site:client-page:query',
          10,
          'COUNTRY_HOME'
        ),
        clientPageItem(
          'country-site-odds-routes',
          '盘口路由',
          '/country-site/routes/odds',
          'country-site:route:odds',
          20,
          'ONLINE_CASINO'
        ),
        clientPageItem(
          'country-site-casino-app-routes',
          '盘口 APP 路由',
          '/country-site/routes/casino-apps',
          'country-site:route:casino-apps',
          30,
          'CASINO_APPS'
        ),
        clientPageItem(
          'country-site-bonus-routes',
          '礼金路由',
          '/country-site/routes/bonuses',
          'country-site:route:bonuses',
          40,
          'CASINO_BONUSES'
        ),
        clientPageItem(
          'country-site-game-routes',
          '游戏路由',
          '/country-site/routes/games',
          'country-site:route:games',
          50,
          'CASINO_GAMES'
        ),
        clientPageItem(
          'country-site-complaint-channel-routes',
          '投诉路由',
          '/country-site/routes/complaints',
          'country-site:route:complaints',
          70,
          'COMPLAINTS'
        ),
        clientPageItem(
          'country-site-freecash-channel-routes',
          'Big Price路由',
          '/country-site/routes/freecash',
          'country-site:route:freecash',
          80,
          'FREECASH'
        ),
        clientPageItem(
          'country-site-community-channel-routes',
          '社区路由',
          '/country-site/routes/community',
          'country-site:route:community',
          90,
          'COMMUNITY'
        ),
        clientPageItem(
          'country-site-tools-channel-routes',
          '工具路由',
          '/country-site/routes/tools',
          'country-site:route:tools',
          120,
          'GAME_TOOLS'
        ),
        clientPageItem(
          'country-site-people-routes',
          '人物列表路由',
          '/country-site/routes/people',
          'country-site:route:characters',
          150,
          'AUTHORS'
        )
      ]
    },
    {
      key: 'country-site-data-config',
      title: '数据配置',
      icon: 'SetUp',
      sortOrder: 30,
      children: [
        item('country-site-route-groups', '所属分组', '/country-site/route-groups', 'site:client-page-group:query', 70, 'CollectionTag'),
        {
          ...item(
            'country-site-page-center',
            '关于我们',
            '/country-site/page-management',
            'country-site:page-center',
            10,
            'Document'
          ),
          authorizationFallbackCode: 'site:client-page:query'
        },
        {
          ...item(
            'country-site-sitemap',
            'Sitemap',
            '/country-site/sitemap',
            'country-site:sitemap',
            20,
            'Document'
          ),
          authorizationFallbackCode: 'site:client-page:query'
        },
        {
          ...item(
            'country-site-social-media',
            '社媒配置',
            '/country-site/social-media',
            'site:social-media:query',
            30,
            'Promotion'
          )
        },
        {
          ...item(
            'country-site-login-page-image',
            '登录页图片',
            '/country-site/login-page-image',
            'country-site:login-page-image',
            40,
            'Picture'
          ),
          authorizationFallbackCode: 'site:client-page:query'
        },
        {
          ...item('country-site-faq-list', 'FAQ清单', '/country-site/faq-list', 'site:faq:query', 50, 'QuestionFilled')
        },
        {
          ...item(
            'country-site-footer-config',
            '页脚配置',
            '/country-site/footer-config',
            'country-site:footer-config',
            60,
            'Document'
          ),
          authorizationFallbackCode: 'site:client-page:query'
        }
      ]
    },
    item('country-site-odds', '盘口管理', '/country-site/odds', 'site:odds:query', 40, 'OfficeBuilding'),
    {
      ...item(
        'country-site-odds-activities',
        'Bonus管理',
        '/country-site/odds-activities',
        'site:odds-activity:query',
        50,
        'Promotion'
      ),
      authorizationFallbackCode: 'site:odds-activity:query'
    },
    {
      key: 'country-site-activities',
      title: '活动管理',
      icon: 'Promotion',
      sortOrder: 55,
      children: [
        {
          ...item(
            'country-site-weekly-tournaments',
            'Weekly锦标赛',
            '/country-site/activities/weekly-tournaments',
            'country-site:activity:weekly-tournament',
            10
          ),
          authorizationFallbackCode: 'site:tournament:query',
          tournamentKind: 'weekly'
        },
        {
          ...item(
            'country-site-daily-tournaments',
            'Daily锦标赛',
            '/country-site/activities/daily-tournaments',
            'country-site:activity:daily-tournament',
            20
          ),
          authorizationFallbackCode: 'site:tournament:query',
          tournamentKind: 'daily'
        },
        {
          ...item(
            'country-site-multiplier-challenges',
            '游戏倍数大挑战',
            '/country-site/activities/multiplier-challenges',
            'country-site:activity:multiplier-challenge',
            30
          ),
          authorizationFallbackCode: 'site:multiplier-challenge:query'
        }
      ]
    },
    {
      key: 'country-site-game-management',
      title: '游戏管理',
      icon: 'Trophy',
      sortOrder: 60,
      children: [
        item('country-site-game-config', '游戏管理', '/country-site/games', 'site:demo-game:config', 10, 'Trophy'),
        {
          ...item(
            'country-site-game-classifications',
            '游戏分类',
            '/country-site/game-classifications',
            'site:game-classification:query',
            20,
            'CollectionTag'
          ),
          authorizationFallbackCode: 'site:demo-game:config'
        },
        {
          ...item(
            'country-site-provider-config',
            '供应商管理',
            '/country-site/providers',
            'country-site:provider:config',
            30,
            'OfficeBuilding'
          ),
          authorizationFallbackCodes: ['site:demo-game:config', 'site:site:query']
        },
        item('country-site-game-topics', '游戏IP主题', '/country-site/game-topics', 'site:demo-game:topic', 40, 'CollectionTag')
      ]
    }
  ]
}

export function getMenuRouteItems() {
  return (Object.entries(adminMenuCatalog) as Array<[AdminMenuScope, AdminMenuItem[]]>).flatMap(([scope, menus]) =>
    menus.flatMap(menu => {
      const pages = menu.children?.length ? menu.children : menu.path ? [menu] : []
      return pages.map(page => ({
        scope: page.frontendScope || scope,
        page,
        parentTitle: menu.children?.length ? menu.title : undefined
      }))
    })
  )
}

export function getFrontendOnlyMenuRouteItems() {
  return getMenuRouteItems().filter(({ page }) =>
    Boolean(page.authorizationFallbackCode || page.authorizationFallbackCodes?.length)
  )
}

const menuCodeAliases: Readonly<Record<string, string>> = {
  'business:activity:weekly-tournament': 'country-site:activity:weekly-tournament',
  'business:activity:daily-tournament': 'country-site:activity:daily-tournament',
  'business:activity:multiplier-challenge': 'country-site:activity:multiplier-challenge',
  'site:tournament:query': 'country-site:activity:weekly-tournament',
  'site:multiplier-challenge:query': 'country-site:activity:multiplier-challenge',
  'business:user:list': 'site:client-user:query',
  'business:record:coin': 'site:transaction:query',
  'business:record:withdrawal': 'site:withdrawal:query',
  'business:record:message': 'center:message-template:query',
  'business:record:sent-message': 'site:message:query',
  'business:casino:list': 'center:odds:query',
  'business:casino:payment-method': 'center:odds-payment-way:query',
  'business:casino:license': 'center:odds-license:query',
  'business:bonus:list': 'center:odds-activity:query',
  'business:game:demo': 'center:demo-game:query',
  'business:game:coin': 'center:coin-game:query',
  'business:game:provider': 'center:game-provider:query',
  'business:game:category': 'center:game-classification:query',
  'business:game:feature': 'center:game-mechanics:query',
  'business:game:ip-topic': 'center:demo-game-topic:query',
  'country-site:site:list': 'site:site:query',
  'country-site:game:config': 'site:demo-game:config',
  'country-site:odds:query': 'site:odds:query',
  'country-site:odds-activity:query': 'site:odds-activity:query',
  'country-site:author:config': 'site:author:query',
  'business:content:post': 'site:post:query',
  'business:content:topic': 'site:post-topic:query',
  'business:content:rating': 'site:review:query',
  'business:audit:post': 'site:post-review:query',
  'business:audit:comment': 'site:post-comment-review:query',
  'business:audit:fb-share': 'site:fb-share-review:query',
  'business:audit:complaint': 'site:complaint:query',
  'business:audit:rating': 'business:audit:game-rating',
  'business:config:country': 'center:country:query',
  'business:config:language': 'center:language:query',
  'business:config:currency': 'center:currency:query',
  'business:config:exchange-rate': 'center:exchange-rate:query',
  'business:content:character': 'center:author-config:query',
  'system:user': 'center:admin-user:manage',
  'system:role': 'center:role:manage',
  'system:menu': 'center:menu:manage',
  'system:log': 'center:operation-log:list',
  'country-site:home:config': 'site:client-page:query',
  'country-site:route:about-us': 'country-site:route:footer'
}

function normalizeMenuCode(menuCode: string) {
  return menuCodeAliases[menuCode] || menuCode
}

export function getAdminMenuRouteItem(menuCode: string | null) {
  if (!menuCode) return undefined
  const normalized = normalizeMenuCode(menuCode)
  return getMenuRouteItems().find(({ page }) => normalizeMenuCode(page.menuCode || '') === normalized)
}

export function isSameAdminMenuCode(left: string | null | undefined, right: string | null | undefined) {
  return Boolean(left && right && normalizeMenuCode(left) === normalizeMenuCode(right))
}

const registeredMenuCodes = new Set(
  getMenuRouteItems()
    .map(({ page }) => page.menuCode)
    .filter((menuCode): menuCode is string => Boolean(menuCode))
)

/** 只展示已在前端目录中注册了路由的动态菜单。 */
export function hasRegisteredMenuRoute(menuCode: string | null) {
  return Boolean(menuCode && (registeredMenuCodes.has(menuCode) || registeredMenuCodes.has(normalizeMenuCode(menuCode))))
}

export function getDefaultMenuPath() {
  const firstMenu = adminMenuCatalog.business[0]
  return firstMenu.children?.[0]?.path || firstMenu.path || '/'
}

export function getAuthorizedDefaultMenuPath(menus: AdminMenu[] = []) {
  const menu = menus.find(item => item.type === 'MENU' && hasRegisteredMenuRoute(item.menuCode))
  return getAdminMenuRouteItem(menu?.menuCode || null)?.page.path || menu?.path || getDefaultMenuPath()
}
